/**
 * Image Uploader Module
 *
 * Handles image upload to S3 and image URL retrieval.
 */

const AWS = require('aws-sdk');

import { v4 as uuidv4 } from 'uuid';
import { generateClient } from 'aws-amplify/api';

import { getToken } from '../auth/tokenManager';
import awsConfig from '../../src/aws-exports';

const buffer = require('buffer').Buffer;

/**
 * Convert a Blob to base64 string
 * @param {Blob} blob - The blob to convert
 * @returns {Promise<string>} Base64 encoded string
 */
export async function blobToBase64(blob) {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Force convert an image to JPEG format using canvas
 * @param {string} base64Image - Base64 encoded image
 * @returns {Promise<string>} JPEG base64 data URI
 */
export async function forceJPEGConversion(base64Image) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);

      // Convert and export image data as JPEG with proper compression
      const jpegDataUri = canvas.toDataURL('image/jpeg', 0.8);
      resolve(jpegDataUri);
    };

    img.onerror = reject;
    img.src = base64Image;
  });
}

/**
 * Get an Amplify client configured for image uploads
 * @returns {Promise<Object>} Configured Amplify client
 */
export async function getImageClient() {
  const headers = {
    Authorization: await getToken(),
    'Content-Type': 'application/octet-stream',
  };

  return generateClient({ headers: headers });
}

/**
 * Get a signed URL for an image
 * @param {string} imageID - The S3 key of the image
 * @param {boolean} extendedExpiration - Use 7-day expiration instead of 5 minutes
 * @param {Function} apiGet - The API get function
 * @param {Function} printAPIError - Error logging function
 * @returns {Promise<string>} The signed URL
 */
export async function getImageURL(imageID, extendedExpiration, apiGet, printAPIError) {
  let expiration = extendedExpiration ? 7 * 24 * 60 * 60 : 60 * 5;
  try {
    return apiGet('Resty', '/items/getImageURL', {
      queryParams: {
        ImageID: imageID,
        Expiration: expiration,
      },
    })
      .then((result) => result)
      .catch((error) => {
        printAPIError(error);
      });
  } catch (e) {
    console.log(e.message);
  }
}

/**
 * Update upload progress for a specific URI
 * @param {string} uri - The image URI
 * @param {string} status - The status ('Picking', 'Uploading', 'Finished', 'Failed')
 * @param {Function} setUploadProgress - State setter for progress
 */
export function setUploadProgressURI(uri, status, setUploadProgress) {
  setUploadProgress((before) => ({ ...before, [uri]: status }));
}

/**
 * Upload multiple images
 * @param {Array<string>} uris - Array of base64 image URIs
 * @param {Function} setUploadStage - State setter for upload stage
 * @param {Function} setUploadProgress - State setter for progress
 * @returns {Promise<Array>} Results of all uploads
 */
export async function uploadImages(uris, setUploadStage, setUploadProgress) {
  setUploadStage('Uploading');
  const uploadPromises = uris.map((uri) => uploadImage(uri, setUploadProgress));
  const uploadedImages = await Promise.allSettled(uploadPromises);
  setUploadStage('Finished');
  return uploadedImages;
}

/**
 * Upload a single image to S3
 * @param {string} uri - Base64 image URI
 * @param {Function} setUploadProgress - State setter for progress
 * @returns {Promise<string>} The S3 key of the uploaded image
 */
export async function uploadImage(uri, setUploadProgress) {
  AWS.config.region = awsConfig.aws_project_region;

  const token = await getToken();

  // Create fresh credentials each time to avoid "Logins don't match" error
  // when switching between login methods (Cognito vs QR).
  // The AWS SDK caches identity IDs in multiple places, so we must:
  // 1. Clear the global credentials
  // 2. Create a new credentials object
  // 3. Clear the cached ID on the new object (clears localStorage/internal cache)
  AWS.config.credentials = null;
  const cognitoIdpEndpoint = `cognito-idp.${awsConfig.aws_cognito_region}.amazonaws.com/${awsConfig.aws_user_pools_id}`;
  const credentials = new AWS.CognitoIdentityCredentials({
    IdentityPoolId: awsConfig.aws_cognito_identity_pool_id,
    Logins: {
      [cognitoIdpEndpoint]: token,
    },
  });

  // Clear any cached identity ID from previous sessions (stored in localStorage)
  credentials.clearCachedId();

  // Get fresh credentials
  await new Promise((resolve, reject) => {
    credentials.get((err) => {
      if (err) {
        console.error('Error getting credentials', err);
        reject(err);
      } else {
        resolve();
      }
    });
  });

  const S3 = new AWS.S3({
    apiVersion: 'latest',
    signatureVersion: 'v4',
    credentials: credentials,
  });

  // Generate a unique file name
  const fileName = uuidv4() + '.jpg';

  try {
    setUploadProgressURI(uri, 'Uploading', setUploadProgress);
    const imageFile = uri.substring(uri.indexOf(',') + 1);

    try {
      let base64string = imageFile;
      let base64buffer = buffer.from(base64string, 'base64');

      // Define the S3 parameters
      const params = {
        Bucket: 'moneta-uploaded-images',
        Key: fileName,
        Body: base64buffer,
        ContentType: 'image/jpg',
      };

      return S3.upload(params)
        .promise()
        .then((result) => {
          setUploadProgressURI(uri, 'Finished', setUploadProgress);
          return result.Key;
        })
        .catch((error) => {
          setUploadProgressURI(uri, 'Failed', setUploadProgress);
          return null;
        });
    } catch (e) {
      console.log(e.message);
    }
  } catch (e) {
    console.log(e.message);
  }
}

/**
 * Check if all uploads completed successfully
 * @param {Object} uploadProgress - Upload progress object
 * @returns {boolean} True if all uploads finished
 */
export function uploadSuccessful(uploadProgress) {
  let didSucceed = true;
  Object.keys(uploadProgress).forEach((key) => {
    if (!(uploadProgress[key] == 'Finished')) {
      didSucceed = false;
    }
  });
  return didSucceed;
}

/**
 * Check if any uploads failed
 * @param {Object} uploadProgress - Upload progress object
 * @returns {boolean} True if any upload failed
 */
export function anyUploadsFailed(uploadProgress) {
  let didFail = false;
  Object.keys(uploadProgress).forEach((key) => {
    if (uploadProgress[key] == 'Failed') {
      didFail = true;
    }
  });
  return didFail;
}

/**
 * Get URLs for uploaded images
 * @param {boolean} tryUploadImage - Whether to attempt upload
 * @param {Array} uploadImages - Previously uploaded images
 * @param {Array} imagesToSend - Images to upload
 * @param {Function} setUploadedImages - State setter
 * @param {Function} setUploadStage - State setter
 * @param {Function} setUploadProgress - State setter
 * @param {Function} apiGet - API get function
 * @param {Function} getImageURLFn - getImageURL function
 * @param {Function} uploadImagesFn - uploadImages function
 * @param {Function} uploadSuccessfulFn - uploadSuccessful function
 * @returns {Promise<Array>} [fullImageURLs, fulfilledImageURLs]
 */
export async function getUploadedImageURLs(
  tryUploadImage,
  uploadImagesArr,
  imagesToSend,
  setUploadedImages,
  setUploadStage,
  setUploadProgress,
  getImageURLFn,
  uploadImagesFn,
  uploadSuccessfulFn,
) {
  let ui = uploadImagesArr;
  let fulfilledImageURLs;

  if (tryUploadImage == true) {
    ui = await uploadImagesFn(imagesToSend, setUploadStage, setUploadProgress);
    setUploadedImages(() => ui);
    fulfilledImageURLs = ui
      .filter((value) => value['status'] == 'fulfilled')
      .map((value) => value['value']);
    if (fulfilledImageURLs.length < imagesToSend.length && !uploadSuccessfulFn(ui)) {
      setUploadStage('Failed');
      return;
    }
  } else {
    fulfilledImageURLs = ui
      .filter((value) => value['status'] == 'fulfilled')
      .map((value) => value['value']);
  }

  let fullImageURLs = [];
  if (fulfilledImageURLs.length > 0) {
    const getImageURLPromises = fulfilledImageURLs.map((imageID) => getImageURLFn(imageID, true));
    fullImageURLs = await Promise.all(getImageURLPromises);
  }

  return [fullImageURLs, fulfilledImageURLs];
}
